"use client";

import { useEffect, useState } from "react";
import { ArrowUpRight, Bike, Bus, CarFront, TrainFront } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Localisation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [language, setLanguage] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("lacolonne_language") || "fr";
    }
    return "fr";
  });
  useEffect(() => {
    const handleLanguageChange = () => {
      const currentLang = localStorage.getItem("lacolonne_language") || "fr";
      setLanguage(currentLang);
    };
    window.addEventListener("storage", handleLanguageChange);
    window.addEventListener("languagechange", handleLanguageChange);
    return () => {
      window.removeEventListener("storage", handleLanguageChange);
      window.removeEventListener("languagechange", handleLanguageChange);
    };
  }, []);
  const isEnglish = language === "en";
  const transports = [{
    icon: Bus,
    label: isEnglish ? "Public Transit · 5 min" : "Transport en commun · 5 min",
    detail: isEnglish ? "ETS Bus routes 1, 8, and 9 in immediate proximity" : "Lignes ETS 1, 8, et 9 à proximité immédiate"
  }, {
    icon: CarFront,
    label: isEnglish ? "By Car · Easy Access" : "Voiture · Accès facile",
    detail: isEnglish ? "Near Wayne Gretzky Drive" : "Près de l'autoroute Wayne Gretzky Drive"
  }, {
    icon: Bike,
    label: isEnglish ? "Bicycle · Bike Paths" : "Vélo · Pistes cyclables",
    detail: isEnglish ? "Secure bicycle parking available" : "Stationnement sécurisé pour vélos disponible"
  }, {
    icon: TrainFront,
    label: isEnglish ? "LRT · 10 min walk" : "LRT · 10 min de marche",
    detail: isEnglish ? "Stadium Station for quick downtown access" : "Station Stadium pour un accès rapide au centre-ville"
  }];
  return <section data-section-id="550" id="localisation" className="dark bg-background text-foreground py-16 md:py-24 lg:py-32 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Map Container */}
          <div className="lg:col-span-3 relative group">
            <div className="relative overflow-hidden rounded-3xl border border-border/80 shadow-2xl h-[440px] sm:h-[540px] lg:h-full min-h-[520px] bg-muted">
              <iframe title={isEnglish ? "Location Association La Colonne" : "Localisation Association La Colonne"} src="https://www.openstreetmap.org/export/embed.html?bbox=-113.55%2C53.52%2C-113.45%2C53.57&layer=mapnik&marker=53.5461%2C-113.4937" className="absolute inset-0 w-full h-full grayscale-[55%] contrast-125 transition-transform duration-700 ease-out group-hover:scale-[1.01]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />

              {/* Atmospheric overlay with site color variables */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-background/80 via-background/20 to-secondary/10" aria-hidden="true" />

              {/* Interactive marker */}
              <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="relative flex items-center justify-center">
                  <span className="absolute inline-flex h-16 w-16 rounded-lg bg-secondary opacity-35 animate-ping" />
                  <span className="absolute inline-flex h-10 w-10 rounded-lg bg-secondary opacity-60 animate-pulse" />
                  <span className="relative inline-flex h-6 w-6 rounded-lg bg-secondary ring-4 ring-background shadow-2xl" />
                </div>
                <div className="mt-3 flex justify-center">
                  <span className="inline-flex items-center gap-1.5 rounded-xl bg-primary text-primary-foreground px-3.5 py-1.5 text-xs font-semibold shadow-xl font-mono tracking-wider border border-secondary/30">
                    <span className="inline-block h-1.5 w-1.5 rounded-lg bg-secondary" />
                    LA COLONNE
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Details Card */}
          <div className="lg:col-span-2 bg-card rounded-3xl p-7 sm:p-10 lg:p-12 flex flex-col border border-border/80 shadow-xl relative overflow-hidden">
            {/* Subtle top accent border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary/80 via-secondary to-secondary/30" />

            <span className="text-xs font-medium tracking-[0.2em] uppercase text-secondary font-mono">
              {isEnglish ? "Find Us" : "Nous Trouver"}
            </span>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-foreground font-default leading-[1.1]">
              {isEnglish ? "In the heart of" : "Au cœur de"}
              <br />
              <span className="italic font-serif text-secondary">Edmonton.</span>
            </h2>

            <p className="mt-5 text-base text-muted-foreground max-w-md font-default">
              {isEnglish ? "Strategically located to serve the francophone community, our offices are a welcoming hub for connection and integration." : "Situés stratégiquement pour servir la communauté francophone, nos bureaux sont un carrefour d'accueil et d'intégration."}
            </p>

            <div className="mt-8 flex-1 flex flex-col">
              {transports.map((t, i) => {
              const Icon = t.icon;
              return <div key={i} data-index={i}>
                    {i > 0 && <Separator className="bg-border/50" />}
                    <div className="flex items-start gap-5 py-4">
                      <div className="flex-shrink-0 flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary/15 text-secondary border border-secondary/20">
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0 pt-1">
                        <p className="text-base font-semibold text-foreground font-default">
                          {t.label}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground font-default">
                          {t.detail}
                        </p>
                      </div>
                    </div>
                  </div>;
            })}
            </div>

            {/* Help Callout Card with generous rounding and secondary background accent */}
            <div className="mt-6 rounded-2xl border border-secondary/25 bg-secondary/10 p-5 backdrop-blur-xs">
              <p className="text-sm text-foreground font-default">
                <span className="font-semibold font-default text-secondary">
                  {isEnglish ? "Need help? " : "Besoin d'aide? "}
                </span>
                {isEnglish ? "Contact us to arrange community ride assistance." : "Contactez-nous pour organiser un transport solidaire."}
              </p>
              <Button variant="link" className="mt-2 h-auto p-0 text-secondary bg-transparent hover:bg-transparent" asChild>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary font-default group cursor-pointer">
                  {isEnglish ? "View full directions" : "Voir l'itinéraire complet"}
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-lg bg-secondary text-secondary-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                    <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
                  </span>
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}